<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import IframeAddAgent from './IframeAddAgent.vue';
import IframeEditAgent from './IframeEditAgent.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { aichatRequest } from '../../../../../shared/helpers/request';
import { getCountry, getSite } from '../../../../../shared/helpers/env';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = ref([]);
const isFetching = ref(false);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');
const accountId = computed(() => getters.getCurrentAccountId.value);

const country = computed(() => getCountry());
const site = computed(() => getSite());

const fetchAgents = async () => {
  isFetching.value = true;
  const agentResponse = await aichatRequest
    .get(`/n/api/buyer/cs/aichat/agents/list?accountId=${accountId.value}`)
    .finally(() => {
      isFetching.value = false;
    });
  agentList.value = agentResponse?.data ?? [];
};
onMounted(async () => {
  store.dispatch('customRole/getCustomRole');
  fetchAgents();
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="$t('AGENT_MGMT.HEADER')"
        :description="$t('AGENT_MGMT.DESCRIPTION')"
        :link-text="$t('AGENT_MGMT.LEARN_MORE')"
        feature-name="agents"
      >
        <template #actions>
          <woot-button
            class="rounded-md button nice"
            icon="add-circle"
            @click="openAddPopup"
          >
            {{ $t('AGENT_MGMT.HEADER_BTN_TXT') }}
          </woot-button>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <p
        v-if="!isFetching && !agentList.length"
        class="flex-1 text-slate-700 dark:text-slate-100 flex items-center justify-center text-base"
      >
        {{ $t('AGENT_MGMT.LIST.404') }}
      </p>
      <div v-else class="agent-table-container">
        <div v-if="isFetching" class="agent-table-loading">
          <woot-loading-state :message="$t('AGENT_MGMT.LOADING')" />
        </div>
        <table class="divide-y divide-slate-75 dark:divide-slate-700">
          <tbody class="divide-y divide-n-weak text-n-slate-11">
            <tr v-for="(agent) in agentList" :key="agent.email">
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <div class="flex flex-row items-center gap-4">
                  <Thumbnail
                    :src="agent.thumbnail"
                    :username="agent.name"
                    size="40px"
                    :status="agent.availability_status"
                  />
                  <div>
                    <span class="block font-medium capitalize">
                      {{ agent.name }}
                    </span>
                  </div>
                </div>
              </td>

              <td class="relative py-4 ltr:pr-4 rtl:pl-4">
                <span
                  class="block font-medium w-fit"
                  :class="{
                    'hover:text-gray-900 group cursor-pointer':
                      agent.custom_role_id,
                  }"
                >
                  {{ getAgentRoleName(agent) }}

                  <div
                    class="absolute left-0 z-10 hidden max-w-[300px] w-auto bg-white rounded-xl border border-slate-50 shadow-lg top-14 md:top-12 dark:bg-slate-800 dark:border-slate-700"
                    :class="{ 'group-hover:block': agent.custom_role_id }"
                  >
                    <div class="flex flex-col gap-1 p-4">
                      <span class="font-semibold">
                        {{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}
                      </span>
                      <ul class="pl-4 mb-0 list-disc">
                        <li
                          v-for="permission in getAgentRolePermissions(agent)"
                          :key="permission"
                          class="font-normal"
                        >
                          {{
                            $t(
                              `CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`
                            )
                          }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </span>
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <div v-if="!agent?.inboxes?.length">-</div>
                <div class="inbox-container" v-else>
                  <span v-for="inbox in agent.inboxes" :key="inbox.id">
                    <img v-if="inbox.icon" :src="inbox.icon" />
                    <span v-else class="inbox-name-icon">
                      {{ inbox.name?.slice(0, 1)?.toUpperCase() || '?' }}
                    </span>
                  </span>
                </div>
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <div v-if="!agent?.teams?.length">-</div>
                <div v-else class="agent-teams">
                  <span v-for="team in agent.teams" :key="team">
                    {{ team }}
                  </span>
                </div>
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <div v-if="agent?.concurrence === null">-</div>
                <div v-else class="agent-concurrence">
                  {{ agent.concurrence }}
                </div>
              </td>
              <td class="py-4">
                <div class="flex justify-end gap-1">
                  <woot-button
                    v-if="showEditAction(agent)"
                    v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                    variant="smooth"
                    size="tiny"
                    color-scheme="secondary"
                    icon="edit"
                    class-names="grey-btn"
                    @click="openEditPopup(agent)"
                  />
                  <!-- <woot-button
                    v-if="showDeleteAction(agent)"
                    v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                    variant="smooth"
                    color-scheme="alert"
                    size="tiny"
                    icon="dismiss-circle"
                    class-names="grey-btn"
                    :is-loading="loading[agent.id]"
                    @click="openDeletePopup(agent, index)"
                  /> -->
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <woot-drawer v-model:show="showAddPopup" :on-close="hideAddPopup">
      <IframeAddAgent
        :account-id="accountId"
        :country="country"
        :site="site"
        @close="hideAddPopup"
        @refresh="fetchAgents"
      />
    </woot-drawer>

    <woot-drawer v-model:show="showEditPopup" :on-close="hideEditPopup">
      <IframeEditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :account-id="accountId"
        :country="country"
        :site="site"
        @close="hideEditPopup"
        @refresh="fetchAgents"
      />
    </woot-drawer>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </SettingsLayout>
</template>
<style scoped lang="scss">
.agent-table-container {
  position: relative;
  min-height: 100px;

  .agent-table-loading {
    position: absolute;
    inset: 0;
    background-color: rgba(255, 255, 255, 0.55);
    z-index: 10;
    backdrop-filter: blur(2px);
  }

  .agent-table-loading span {
    position: absolute;
    top: 50%;
    left: 50%;
  }
}

.inbox-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  max-width: 110px;

  img,
  .inbox-name-icon {
    height: 20px;
    width: 20px;
    border-radius: 4px;
  }
}

.inbox-name-icon {
  display: inline-block;
  text-align: center;
  background-color: #e5e7eb;
  font-size: 12px;
  font-weight: bolder;
  line-height: 20px;
  color: #60646c;
}

.agent-teams {
  display: flex;
  flex-direction: column;
  font-size: 14px;
  font-weight: 500;

  span {
    text-transform: capitalize;
  }
}

.agent-concurrence {
  font-size: 14px;
  font-weight: 500;
}
</style>
